import { filterFor } from '@thom/pb/filter-builder'
import { withBlocked } from '@thom/folio-domain/core/domain/blocked'
import { countByParent } from '@thom/folio-domain/core/domain/child-count'
import { entryId as toEntryId } from '@thom/folio-domain/core/domain/entry'
import type { Issue, IssueKind, IssueStatus, Priority, Size, WayfinderType } from '@thom/folio-domain/core/domain/issue'
import { isSize, issueId as toIssueId } from '@thom/folio-domain/core/domain/issue'
import { inScope } from '@thom/folio-domain/core/domain/issue-scope'
import { planId as toPlanId } from '@thom/folio-domain/core/domain/plan'
import { projectId as toProjectId, userId as toUserId } from '@thom/folio-domain/core/domain/project'
import type { IssueFilter, IssuesPort } from '@thom/folio-domain/core/ports/issues'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { tryCatch } from '@thom/folio-domain/lib/try-catch'
import { z } from '@thom/folio-domain/lib/zod'
import { Collections, type JournIssuesResponse } from '@thom/folio-ui/pocketbase-types'
import { getPocketBaseClient } from './client'
import { countRows } from './count-rows'
import { paginate } from './paginate'
import { projectClauses } from './project-clauses'
import { linksOf, tagsByTarget } from './relations'
import { keyed } from './request-key'
import { sortExpr } from './sort'

type IssueRecord = JournIssuesResponse<string[]>

type IssueColumns = {
	id: string
	'project.slug': string
	'project.domain.slug': string
	'project.domain.client.slug': string
	kind: IssueKind
	plan: string
	parent: string
	slug: string
	title: string
	body: string
	status: IssueStatus
	priority: Priority
	archived: boolean
	created: Date
	updated: Date
}

const createdSchema = z.object({ slug: z.string() })

const entrySchema = z.object({ id: z.string() })

const toIssue = (record: IssueRecord): Issue => ({
	id: toIssueId(record.id),
	kind: record.kind as IssueKind,
	projectId: toProjectId(record.project),
	planId: record.plan ? toPlanId(record.plan) : undefined,
	slug: record.slug,
	title: record.title,
	body: record.body ?? '',
	status: record.status as IssueStatus,
	priority: record.priority as Priority,
	size: record.size && isSize(record.size) ? (record.size as Size) : undefined,
	assignee: record.assignee ? toUserId(record.assignee) : undefined,
	tags: [],
	position: record.position ?? 0,
	dueDate: record.due_date ? new Date(record.due_date) : undefined,
	wayfinder: record.wayfinder ? (record.wayfinder as WayfinderType) : undefined,
	externalRef: record.external_ref ?? '',
	resolution: record.resolution ?? '',
	resolutionEntry: record.resolution_entry ? toEntryId(record.resolution_entry) : undefined,
	createdBy: record.created_by ? toUserId(record.created_by) : undefined,
	createdAt: new Date(record.created),
	updatedAt: new Date(record.updated),
	parentId: record.parent ? toIssueId(record.parent) : undefined,
	childCount: 0,
	dependsOn: [],
	relatedTo: [],
	blocked: false,
	archived: record.archived ?? false,
})

const hydrate = async (issues: readonly Issue[]): Promise<readonly Issue[]> => {
	if (issues.length === 0) return issues

	const ids = issues.map(issue => issue.id as string)
	const [tags, links] = await Promise.all([tagsByTarget('issue', ids), linksOf(ids)])

	const linked = issues.map(issue => {
		const id = issue.id as string
		return {
			...issue,
			tags: tags.get(id) ?? [],
			dependsOn: (links.dependsOn.get(id) ?? []).map(toIssueId),
			relatedTo: (links.relatedTo.get(id) ?? []).map(toIssueId),
		}
	})

	return withBlocked(linked)
}

const hydrateOne = async (issue: Issue): Promise<Issue> => {
	const [hydrated] = await hydrate([issue])
	return hydrated ?? issue
}

const matching = (project: string, filter: IssueFilter) =>
	filterFor<IssueColumns>()([
		...projectClauses(project),
		{ field: 'id', comparator: 'eq', value: filter.id },
		{ field: 'kind', comparator: 'eq', value: filter.kind },
		{ field: 'plan', comparator: 'eq', value: filter.planId },
		{ field: 'parent', comparator: 'eq', value: filter.parentId },
		{ field: 'status', comparator: 'anyOf', value: filter.status },
		{ field: 'priority', comparator: 'eq', value: filter.priority },
		{ field: 'title', comparator: 'contains', value: filter.search },
		{ field: 'archived', comparator: 'eq', value: filter.id === undefined ? filter.archived === true : undefined },
	])

const columns = (project: string, filter: IssueFilter) => {
	const { expr, params } = matching(project, filter)

	return { expr: filter.roots ? `${expr} && parent = ""` : expr, params }
}

const withChildCounts = async (
	issues: readonly Issue[],
	project: string,
	filter: IssueFilter,
): Promise<readonly Issue[]> => {
	if (issues.length === 0) return issues

	const client = getPocketBaseClient()
	const { expr, params } = matching(project, { ...filter, parentId: undefined, search: undefined, tags: undefined })

	const rows = await client
		.collection(Collections.JournIssues)
		.getFullList<Pick<IssueRecord, 'parent'>>(
			keyed('issues.childCounts', { filter: client.filter(`${expr} && parent != ""`, params), fields: 'parent' }),
		)
	const counts = countByParent(rows.map(row => row.parent))

	return issues.map(issue => ({ ...issue, childCount: counts.get(issue.id) ?? 0 }))
}

const createIssuesAdapter = (): IssuesPort => {
	const client = getPocketBaseClient()
	const collection = () => client.collection(Collections.JournIssues)

	return {
		count: async (project, filter = {}): Promise<Result<number>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				countRows(collection(), keyed('issues.count', { filter: client.filter(expr, params) })),
			)

			return error ? err(new Error(`Failed to count issues: ${error.message}`, { cause: error })) : ok(data)
		},

		list: async (project, filter = {}): Promise<Result<readonly Issue[]>> => {
			const { expr, params } = columns(project, filter)

			const { data, error } = await tryCatch(
				paginate<IssueRecord>(
					collection(),
					filter,
					keyed(
						'issues.list',
						{
							filter: client.filter(expr, params),
							sort: sortExpr(filter.sort, '-created'),
						},
						// tags narrow the rows after they arrive, and the paging is
						// applied by paginate, so neither reaches the filter expression
						// the key is otherwise built from.
						[filter.limit, filter.offset, filter.tags],
					),
				),
			)
			if (error) return err(new Error(`Failed to list issues: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(hydrate(data.map(toIssue)))
			if (hydrated.error) {
				return err(new Error(`Failed to load issue relations: ${hydrated.error.message}`, { cause: hydrated.error }))
			}

			const scoped = hydrated.data.filter(issue => inScope(issue, filter))
			if (!filter.roots && filter.parentId === undefined) return ok(scoped)

			const counted = await tryCatch(withChildCounts(scoped, project, filter))
			return counted.error
				? err(new Error(`Failed to count child issues: ${counted.error.message}`, { cause: counted.error }))
				: ok(counted.data)
		},

		get: async (project, slug): Promise<Result<Issue>> => {
			const { expr, params } = filterFor<IssueColumns>()([
				...projectClauses(project),
				{ field: 'slug', comparator: 'eq', value: slug },
			])

			const { data, error } = await tryCatch(
				collection().getFirstListItem<IssueRecord>(client.filter(expr, params), keyed('issues.get', {})),
			)
			if (error) return err(new Error(`Failed to load issue ${slug}: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(hydrateOne(toIssue(data)))
			return hydrated.error
				? err(new Error(`Failed to load issue relations: ${hydrated.error.message}`, { cause: hydrated.error }))
				: ok(hydrated.data)
		},

		getById: async (project, id): Promise<Result<Issue>> => {
			const { expr, params } = filterFor<IssueColumns>()([...projectClauses(project)])

			const { data, error } = await tryCatch(
				collection().getOne<IssueRecord>(id, keyed(`issues.getById.${id}`, { filter: client.filter(expr, params) })),
			)
			if (error) return err(new Error(`Failed to load issue ${id}: ${error.message}`, { cause: error }))

			const hydrated = await tryCatch(hydrateOne(toIssue(data)))
			return hydrated.error
				? err(new Error(`Failed to load issue relations: ${hydrated.error.message}`, { cause: hydrated.error }))
				: ok(hydrated.data)
		},

		create: async (project, input): Promise<Result<{ readonly slug: string }>> => {
			const { data, error } = await tryCatch(
				client.send(`/api/folio/projects/${encodeURIComponent(project)}/issues`, {
					method: 'POST',
					body: input,
				}),
			)
			if (error) return err(new Error(`Could not create the ticket: ${error.message}`, { cause: error }))

			const parsed = createdSchema.safeParse(data)

			return parsed.success ? ok({ slug: parsed.data.slug }) : err(new Error('The server sent an unexpected answer.'))
		},

		update: async (id, input): Promise<Result<void>> => {
			const { error } = await tryCatch(
				client.send(`/api/folio/issues/${encodeURIComponent(id)}`, {
					method: 'PATCH',
					body: { title: input.title, body: input.body, priority: input.priority, size: input.size ?? 0 },
				}),
			)

			return error ? err(new Error(`Could not save the issue: ${error.message}`, { cause: error })) : ok(undefined)
		},

		setStatus: async (id, status): Promise<Result<void>> => {
			const { error } = await tryCatch(
				client.send(`/api/folio/issues/${encodeURIComponent(id)}`, { method: 'PATCH', body: { status } }),
			)

			return error ? err(new Error(error.message, { cause: error })) : ok(undefined)
		},

		resolve: async (projectId, id, input): Promise<Result<void>> => {
			const detail = input.detail.trim()
			let entryId: string | undefined

			if (detail !== '') {
				const created = await tryCatch(
					client.send(`/api/folio/projects/${encodeURIComponent(projectId)}/entries`, {
						method: 'POST',
						body: { kind: 'resolution', issue_id: id, body: detail },
					}),
				)
				if (created.error) {
					return err(new Error(`Could not save the detail: ${created.error.message}`, { cause: created.error }))
				}

				const parsed = entrySchema.safeParse(created.data)
				if (!parsed.success) return err(new Error('The server sent an unexpected answer.'))
				entryId = parsed.data.id
			}

			const { error } = await tryCatch(
				client.send(`/api/folio/issues/${encodeURIComponent(id)}`, {
					method: 'PATCH',
					body: { status: input.status, resolution: input.answer, resolution_entry_id: entryId },
				}),
			)
			if (!error) return ok(undefined)

			if (entryId) {
				await tryCatch(client.send(`/api/folio/entries/${encodeURIComponent(entryId)}`, { method: 'DELETE' }))
			}

			return err(new Error(error.message, { cause: error }))
		},

		setArchived: async (id, archived): Promise<Result<void>> => {
			const { error } = await tryCatch(
				client.send(`/api/folio/issues/${encodeURIComponent(id)}`, { method: 'PATCH', body: { archived } }),
			)

			return error
				? err(new Error(`Could not ${archived ? 'archive' : 'restore'} the issue: ${error.message}`, { cause: error }))
				: ok(undefined)
		},

		remove: async (id): Promise<Result<void>> => {
			const { error } = await tryCatch(client.send(`/api/folio/issues/${encodeURIComponent(id)}`, { method: 'DELETE' }))

			return error ? err(new Error(`Could not delete the issue: ${error.message}`, { cause: error })) : ok(undefined)
		},
	}
}

export { columns, createIssuesAdapter }
